import { defineStore } from 'pinia';
import {
  computed,
  ref,
} from 'vue';

import { buildingsApiConfig } from '@/config/api/buildings';
import { useClientApi } from '@/plugins/api/lk';
import { fullLogDecorate } from '@/shared/lib';
import { useProjectsStore } from '@/store/projects';

import type {
  Building,
  BuildingResponse,
} from './types';

export const PROJECT_KINDS = {
  RESIDENTIAL: 'RESIDENTIAL',
  PARKING: 'PARKING',
  OFFICE: 'OFFICE',
} as const;

export const useBuildingsStore = defineStore('building', () => {
  const buildings = ref<Array<Building> | undefined>(undefined);

  const buildingsWithFlats = computed<Array<Building>>(
    () => buildings.value?.filter(({ flatsCount }) => flatsCount) ?? [],
  );

  const specsBuildings = computed(() => {
    return buildingsWithFlats.value?.map(({ title, id }) => ({
      label: title,
      value: id,
    }));
  });

  const getBuildingById = computed(() => (buildingId: number): Building | undefined => {
    return buildingsWithFlats.value?.find(({ id }) => id === Number(buildingId));
  });

  const fetching = ref(false);

  /**
   * Получение корпусов для шахматки по признаку isDisplayChessboardLkb проекта
   */
  async function getBuildings (projectId: number): Promise<void> {
    fetching.value = true;
    const { getProjectById } = useProjectsStore();
    const project = getProjectById(projectId);

    if (!project?.isDisplayChessboardLkb) {
      buildings.value = [];

      return;
    }

    const url = buildingsApiConfig.list;
    const { data } = await useClientApi().get<Array<BuildingResponse>>(url, {
      params: {
        project: project?.slug,
        kind: [PROJECT_KINDS.RESIDENTIAL],
      },
    });

    buildings.value = data?.length
      ? data.map((building: BuildingResponse) => ({
          ...building,
          title: building.nameDisplay ?? building.name,
        }))
      : [];
    fetching.value = false;
  }

  async function getBuildingsByProjectSlug (projectSlug: string): Promise<void> {
    const url = buildingsApiConfig.list;
    const { data } = await useClientApi().get<Array<BuildingResponse>>(url, {
      params: {
        project: projectSlug,
        kind: [PROJECT_KINDS.RESIDENTIAL],
      },
    });

    buildings.value = data?.length
      ? data.map((building: BuildingResponse) => ({
          ...building,
          title: building.nameDisplay ?? building.name,
        }))
      : [];
  }

  function reset (): void {
    buildings.value = undefined;
  }

  return {
    fetching,
    buildings,
    buildingsWithFlats,
    specsBuildings,
    getBuildingById,
    getBuildings: fullLogDecorate(getBuildings, '🚀 ~ file: buildings.ts ~ getBuildings ~ error', true),
    getBuildingsByProjectSlug: fullLogDecorate(getBuildingsByProjectSlug, '🚀 ~ file: buildings.ts ~ getBuildingsByProjectSlug ~ error', true),
    reset,
  };
});
